import { callApi } from '@/api/api.config';
import { buildPageSortFilterParams } from '@/api/api.params';
import { PaginatedResponse } from '@/api/api.types';
import { Owner } from '@/modules/Users/Owner.schema';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { ColumnFiltersState, PaginationState, SortingState } from '@tanstack/react-table';

export type Customer = {
  client_id: string;
  client_name: string;
  owner: Owner | null;
  client_logo_url:
    | {
        original: string;
        thumbnails: {
          small: string;
          medium: string;
          large: string;
        };
        url: string; // For backward compatibility
      }
    | string
    | null;
};

export const useCustomerList = (pagination: PaginationState, sorting: SortingState, columnFilters: ColumnFiltersState) => {
  return useQuery({
    queryKey: ['CustomerList', pagination, sorting, columnFilters],
    queryFn: () => fetchCustomerListData(pagination, sorting, columnFilters),
    placeholderData: keepPreviousData,
  });
};

const fetchCustomerListData = async (
  pagination: PaginationState,
  sorting: SortingState,
  columnFilters: ColumnFiltersState
): Promise<PaginatedResponse<Customer>> => {
  return callApi({
    method: 'GET',
    url: `customer/customers/`,
    params: buildPageSortFilterParams({ pagination, sorting, columnFilters }),
  }).then((response) => response.data);
};

export const useCustomer = (customerId?: string) => {
  return useQuery({
    queryKey: ['Customer', customerId],
    queryFn: () => (customerId ? fetchCustomerData(customerId) : Promise.resolve(undefined)),
    enabled: !!customerId,
  });
};

const fetchCustomerData = async (customerId: string): Promise<Customer> => {
  return callApi({ method: 'GET', url: `customer/customers/${customerId}/` }).then((response) => response.data);
};

export type ProjectReportingType = 'farm_only' | 'project_only' | 'project_and_farm';

export type Project = {
  project_id: string;
  project_name: string;
  owner: Owner | null;
  status: string;
  reporting_enabled: boolean;
  breadcrumb: {
    client: {
      id: string;
      name: string;
    };
    project: {
      id: string;
      name: string;
    };
    farm: {
      id: string;
      name: string;
    };
  };
  reporting_type?: ProjectReportingType;
  farm_report_subtitle?: string;
  farm_report_introduction?: string;
  project_report_subtitle?: string;
  project_report_introduction?: string;
  report_disclaimer?: string;
  project_csv_enabled?: boolean;
  farm_csv_enabled?: boolean;
  sampling_event?: number | null;
};

export const useProjectList = (
  pagination: PaginationState,
  sorting: SortingState,
  columnFilters: ColumnFiltersState,
  customerId?: string
) => {
  return useQuery({
    queryKey: ['ProjectList', customerId ?? 'all', pagination, sorting, columnFilters],
    queryFn: () => fetchProjectListData(pagination, sorting, columnFilters, customerId),
    placeholderData: keepPreviousData,
  });
};

const fetchProjectListData = async (
  pagination: PaginationState,
  sorting: SortingState,
  columnFilters: ColumnFiltersState,
  customerId?: string
): Promise<PaginatedResponse<Project>> => {
  const url = customerId ? `customer/customer-projects/${customerId}/` : `customer/projects/`;
  return callApi({
    method: 'GET',
    url,
    params: buildPageSortFilterParams({ pagination, sorting, columnFilters }),
  }).then((response) => response.data);
};

export const useProject = (projectId: string) => {
  return useQuery({
    queryKey: ['Project', projectId],
    queryFn: () => fetchProjectData(projectId),
    enabled: !!projectId,
  });
};

const fetchProjectData = async (projectId: string): Promise<Project> => {
  return callApi({ method: 'GET', url: `customer/projects/${projectId}/` }).then((response) => response.data);
};

export const updateProjectSettings = async (projectId: string, data: Record<string, unknown>): Promise<Project> => {
  return callApi({
    method: 'PATCH',
    url: `customer/projects/${projectId}/`,
    data,
  }).then((response) => response.data);
};
